import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@user/components/Header";
import Footer from "@user/components/Footer";
import { api } from "@/lib/api";
import { FileText, ChevronRight, Sparkles } from "lucide-react";

/**
 * My Consultation Records — a personal library of every published LCR the
 * user has received. Reads GET /api/me/consultation-records.
 */
export default function MyRecords() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/me/consultation-records")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-6">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#D4AF37]">Your Legal Library</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540] mt-1" style={{ fontFamily: "Poppins" }}>
            My Consultation Records
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Every published Legal Consultation Record from your advocates in one place.
          </p>
        </div>

        {items === null && (
          <div className="p-10 text-center text-slate-400" data-testid="records-loading">Loading…</div>
        )}
        {items && items.length === 0 && (
          <div className="p-10 text-center border-2 border-dashed border-slate-200 rounded-2xl" data-testid="records-empty">
            <Sparkles size={22} className="mx-auto text-slate-400 mb-2"/>
            <p className="text-sm font-semibold text-slate-600">No records yet</p>
            <p className="text-xs text-slate-500 mt-1">Your first Legal Consultation Record will show up here after a consultation is completed.</p>
            <Link to="/lawyers" className="mt-4 inline-block text-xs font-bold text-[#0A2540] hover:underline">Browse lawyers →</Link>
          </div>
        )}
        {items && items.length > 0 && (
          <ul className="space-y-2" data-testid="records-list">
            {items.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/consultations/${r.consultation_id}/record`}
                  className="flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-2xl hover:border-[#0A2540] hover:shadow-sm transition"
                  data-testid={`record-${r.id}`}
                >
                  <div className="h-10 w-10 rounded-xl bg-[#FBF4DF] text-[#8a6d14] grid place-items-center">
                    <FileText size={16}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-extrabold text-[#0A2540] truncate">{r.matter_title}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {r.lawyer_name} · {r.practice_area || "General"} · {r.date}
                    </p>
                  </div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">v{r.version}</span>
                  <ChevronRight size={16} className="text-slate-400"/>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Footer />
    </div>
  );
}
